import { IGrade } from './gradeModel';
import { IDropDownOption, IFormField } from './main.interface';

export type { IGrade };

/**
 * Student lifecycle status enum matching the backend contract.
 * GRADUATED and DROP_OFF are terminal "school leaver" states that can only be
 * set through the re-enrollment transition flow (never from profile forms).
 */
export enum STUDENT_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  GRADUATED = 'GRADUATED',
  DROP_OFF = 'DROP_OFF',
}

export interface IStudentData {
  id: string;
  full_name: string;
  last_name: string | null;
  display_name: string;
  invitation: string;
  // False when the student has neither a real email nor a guardian with an
  // email — there is no address to deliver credentials to, so they cannot be
  // invited (the Invitation column shows a distinct "No Email" state).
  canBeInvited: boolean;
  gender: string;
  // Grouped academic info (current / previous / requested) — replaces the flat
  // grade / requestedGrade / lastGrade / lastCurriculum / classroom / curriculum
  // / isUnassigned fields. Use resolveStudentAcademicView() to display it.
  academic_informations?: IStudentAcademicInformations;
  name?: string;
  image: string;
  age: number;
  upload?: string;
  guardians: [
    {
      name: string;
      relationship: string;
    },
  ];
  dob: string | null;
  address: string | null;
  email: string | null;
  admission_date: string | null;
  allergies: string | null;
  admission_no: string;
  medication: string | null;
  create_user: string;
  created_at: string;
  updated_user: string | null;
  updated_at: string | null;
  is_deleted: boolean | null;
  delete_user: string | null;
  deleted_at: string | null;
  classId: number;
  status: STUDENT_STATUS;
  bloodId: number | null;
  roomId: number | null;
  programId: number | null;
}
export interface ICardDetails {
  active: number;
  inactive: number;
  withhold: number;
  withdrawn: number;
}

export interface StudentFormProps {
  onSave: (studentData: ICreateStudentRequestBody, createAnother?: boolean) => void;
  onClose?: () => void;
  setUnsavedChanges: (unsaved: boolean) => void;
}

export interface IStudentCreateForm {
  firstName: IFormField<string>;
  lastName: IFormField<string>;
  displayName: IFormField<string>;
  email: IFormField<string>;
  gender: IFormField<IDropDownOption | null>;
  curriculum: IFormField<IDropDownOption | null>;
  requestedGradeId: IFormField<IDropDownOption | null>;
}

export interface ICreateStudentResponse {
  status?: boolean;
  message: string;
  data: {
    students: {
      id: string;
      full_name: string;
    }[];
  };
}

export interface ICreateStudentRequestBody {
  firstName: string;
  lastName: string;
  displayName: string;
  gender: string;
  medium?: string; // Add the correct type for medium here
  curriculumId: string;
  requestedGradeId: string;
  status: STUDENT_STATUS;
  email?: string;
}

export interface IClassroom {
  id: string;
  name: string;
  gradeId: string;
  gradeName: string;
}

export interface ISendInvitationResponse {
  message: string;
  status: string;
}

export interface IGetGradesResponse {
  status: string;
  message: string;
  data: {
    classTempate: IGrade[]; // Adjust this according to the actual response structure
    warning?: string;
  };
}

export interface IGetClassroomResponse {
  status: string;
  message: string;
  data?: {
    classrooms: IClassroom[]; // Adjust this according to the actual response structure
  };
}

export interface IMedium {
  id: string;
  medium: string;
  organization_id?: string;
}

export interface IGetMediumsResponse {
  status: number;
  message: string;
  data: {
    medium: IMedium[];
  };
}

export interface IgetStudentDetailsRequestBodyParams {
  page?: number;
  pageSize?: string;
  search?: string;
  grade?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  medium?: string | null;
  classroom?: string | null;
  curriculumId?: string | string[];
  isUnassigned?: boolean | null;
}

export interface IUpdateStudentDetailsRequestBodyParams {
  full_name?: string;
  last_name?: string;
  assert_id?: string;
  display_name?: string;
  gender?: string;
  grade?: string;
  medium?: string;
  dob?: string;
  address?: string;
  blood?: string;
  admission_date?: string;
  allergies?: string;
  create_admission?: string;
  running_no?: string;
  medication?: string;
  status?: STUDENT_STATUS;
  classroom?: string;
  communication_email?: string;
  emergency_name?: string;
  country_code?: string;
  emergency_contact?: string;
  mother_name?: string;
  father_name?: string;
  mother_email?: string;
  father_email?: string;
  mother_contact?: string;
  father_contact?: string;
  mother_country_code?: string;
  father_country_code?: string;
  Guardian?: GuardianDetails;
}

export interface GuardianDetails {
  mother?: GuardianContact;
  father?: GuardianContact;
}

export interface GuardianContact {
  mother_firstname?: string;
  mother_lastname?: string;
  father_firstname?: string;
  father_lastname?: string;
  mother_email?: string;
  father_email?: string;
  mother_contact?: string;
  father_contact?: string;
  mother_country_code?: string;
  father_country_code?: string;
}

export interface IUpdateStudentDetailsResponseBodyParams {
  status: number;
  message: string;
  data: {
    student: IStudentSoftDelete[];
  };
}

export interface IStudentSoftDelete {
  id: string;
  full_name: string;
}

export interface IStudentSoftDeleteResponseBody {
  status: number;
  message: string;
  data: {
    student: IStudentSoftDelete[];
  };
}
export interface IStudentSoftDeleteRequestBody {
  id: string;
}

export interface IStudentNextNumberResponseBody {
  status: number;
  message: string;
  data?: {
    Admission_no: {
      next_no: string;
    };
  };
}
export interface IStudentSoftDeleteResponseBody {
  status: number;
  message: string;
}
export interface IGetStudentsResponseBody {
  status: number;
  message: string;
  data?: {
    students: IStudentData[];
    total: number;
    currentPage: number;
    pageSize: number;
    cardDetails: ICardDetails;
  };
  //total: number;
}

export interface ICreateStudentResponseBody {
  status: string;
  message: string;
  data: IStudentData;
}

export interface ICreateStudentError {
  status?: number;
  message?: string;
  description?: string;
}

export interface ICreateStudentError {
  message?: string;
}

export interface GuardianDetailsById {
  mother: {
    firstname: string;
    lastname: string;
    email: string;
    contact: string;
  };
  father: {
    firstname: string;
    lastname: string;
    email: string;
    contact: string;
  };
}

export interface IGetStudentByIdResponse {
  status: number;
  message: string;
  data: {
    student: IStudent;
  };
}

// ========== STUDENT DETAILS ==========
export interface IStudentFormData {
  status: IFormField<IDropDownOption | null>;
  studentId: IFormField<string>;
  firstName: IFormField<string>;
  lastName: IFormField<string>;
  displayName: IFormField<string>;
  email: IFormField<string>;
  curriculum: IFormField<string>;
  grade: IFormField<string>;
  requestedGrade: IFormField<string>;
  classroom: IFormField<string>;
  gender: IFormField<IDropDownOption | null>;
  dob: IFormField<Date | null>;
  address: IFormField<string>;
  blood: IFormField<IDropDownOption | null>;
  admissionDate: IFormField<Date | null>;
  admissionNo: IFormField<string>;
  medication: IFormField<string>;
  allergies: IFormField<string>;
  emergencyName: IFormField<string>;
  emergencyContact: IFormField<string>;
}

/** A single grade/classroom/curriculum reference in an academic info block. */
export interface IAcademicInfoRef {
  id: string;
  name: string;
}

/**
 * One "phase" of a student's academic information — grade, classroom and
 * curriculum for that phase (current / previous / requested).
 */
export interface IAcademicInfoBlock {
  grade: IAcademicInfoRef;
  classroom: IAcademicInfoRef;
  curriculum: IAcademicInfoRef | null;
}

/**
 * Grouped academic information on the student profile:
 * - `current`   — the active classroom assignment (or requested-grade fallback)
 * - `previous`  — the last classroom the student transitioned out of
 * - `requested` — the grade/curriculum requested at admission
 */
export interface IStudentAcademicInformations {
  current: IAcademicInfoBlock;
  previous: IAcademicInfoBlock;
  requested: IAcademicInfoBlock;
}

export interface IStudent {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  gender: string;
  medium: string;
  dob: string;
  address: string;
  blood: string;
  email: string | null;
  image: string;
  image_id: string;
  admission_date: string;
  allergies: string;
  medication: string;
  status: STUDENT_STATUS;
  academic_informations: IStudentAcademicInformations;
  emergency_name: string;
  emergency_country_code: string;
  emergency_contact: string;
  admission_no: string;
}

export interface IGetStudentByIdResponse {
  status: number;
  message: string;
  data: {
    student: IStudent;
  };
}

export interface IStudentUpdatePayload {
  first_name: string;
  last_name: string;
  assert_id: string;
  display_name: string;
  gender: string;
  dob: string | null; // Using string for "YYYY-MM-DD" format
  address: string;
  blood: string;
  admission_date: string | null; // Using string for "YYYY-MM-DD" format
  allergies: string;
  admission_no: string;
  medication: string;
  // Optional real email for the student's web login. Sent as empty string to clear.
  email?: string;
  // Omitted for school leavers (Graduated / Drop Off) — terminal statuses are
  // read-only and the backend rejects them in update payloads.
  status?: STUDENT_STATUS;
  emergency_name: string;
  emergency_contact: string;
}

export interface IGetGuardiansReposne {
  status: number;
  message: string;
  data: {
    guardians: IStudentGuardian[];
  };
}

export interface IStudentGuardian {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  relationship: string;
  isPrimary: boolean;
}

/**
 * Single Student Activation Result
 */
export interface IStudentActivationResult {
  student_id: string;
  student_name: string;
  success: boolean;
  message: string;
  error?: string;
  already_active?: boolean;
}

/**
 * Bulk Activation Response Data
 */
export interface IBulkActivationData {
  total_requested: number;
  total_activated: number;
  total_failed: number;
  activation_results: IStudentActivationResult[];
}

/**
 * Bulk Activation API Response
 */
export interface IBulkActivationResponse {
  total_requested: number;
  total_activated: number;
  total_failed: number;
  activation_results: IStudentActivationResult[];
}

/**
 * Bulk Activation API Response Wrapper
 */
export interface IBulkActivateStudentsResponse {
  status: number;
  message: string;
  data: IBulkActivationResponse;
}
/**
 * Props for StudentActivationSummary Component
 */
export interface IStudentActivationSummaryProps {
  activationResults: IBulkActivationResponse | null;
}

/**
 * Payload for sending (or resending) login-credential invitations to students.
 * The backend routes each recipient (student's own email / guardian / skip).
 */
export interface ISendStudentInvitationPayload {
  studentIds: string[];
}

/** Per-request summary returned by the student invitation endpoint. */
export interface IStudentInvitationSummary {
  total: number;
  sent: number;
  skipped: number;
  failed: number;
}

export interface ISendStudentInvitationResponse {
  status: number;
  message: string;
  data: IStudentInvitationSummary;
}
